/* eslint-disable no-case-declarations */
/* eslint-disable import/prefer-default-export */
import { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import mAccount from 'models/cpm/account';
import useMutation from 'hooks/useMutation';
import { useSelector, useDispatch } from 'react-redux';
import { parse, isValidNumber } from 'libphonenumber-js';
import _get from 'lodash/get';
import { useToast } from 'components/toast/Toast';

const INITIAL_FIELDS = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  phoneWithoutPrefix: '',
  countryCode: '',
  password: '',
  passwordConfirmation: '',
};

const INITIAL_ERRORS = {
  firstname: null,
  lastname: null,
  email: null,
  phone: null,
  password: null,
  passwordConfirmation: null,
};

export const useSignUp = ({
  link,
  handleFormTypeChange,
}) => {
  const { formatMessage: t } = useIntl();
  const { showToast, toast } = useToast();
  const slugId = useSelector((state) => _get(state, 'cpmSlug.current.result.id', null));
  const locale = useSelector((state) => _get(state, 'intl.locale', null));

  const dispatch = useDispatch();

  // 📝 Manage fields
  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [clientErrors, setClientErrors] = useState(INITIAL_ERRORS);
  const [submitStatus, setSubmitStatus] = useState(null);

  const canSubmit = Object.keys(fields).every((key) => fields[key] !== '');

  // 🔄 Sign up
  let query = mAccount.mutSignUp;
  if (link) query = mAccount.mutValidateCustomerAccountLink; // Variant, only if customer invitation

  const {
    exec: signUp,
    loading: mutationLoading,
    errors: mutationErrors,
    data: mutationData,
  } = useMutation({
    query,
  });

  // 🔍 Validate fields
  const validateField = (name, value) => {
    switch (name) {
      case 'firstname':
        if (!value.trim()) return t({ id: 'formError.firstname.cantBeBlank' });
        if (value.length < 2) return t({ id: 'formError.firstname.mustBeAtLeastTwoCharacters' });
        return '';
      case 'lastname':
        if (!value.trim()) return t({ id: 'formError.lastname.cantBeBlank' });
        if (value.length < 2) return t({ id: 'formError.lastname.mustBeAtLeastTwoCharacters' });
        return '';
      case 'email':
        if (!value.trim()) return t({ id: 'formError.email.cantBeBlank' });
        const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!REGEX_EMAIL.test(value)) return t({ id: 'formError.email.badFormat' });
        return '';
      case 'phone':
        const cleanedPhone = fields.phoneWithoutPrefix.replace(/[^\d]/g, '');
        if (!cleanedPhone) return t({ id: 'formError.phone.cantBeBlank' });
        const isValid = isValidNumber(parse(value));
        if (!isValid) return t({ id: 'formError.phone.isInvalid' });
        return '';
      case 'password':
        if (!value.trim()) return t({ id: 'formError.password.cantBeBlank' });
        if (value.length < 8) return t({ id: 'formError.password.shouldBeAtLeast8Characters' });
        if (fields.passwordConfirmation && fields.passwordConfirmation !== value) return t({ id: 'formError.password2.notTheSame' });
        return '';
      case 'passwordConfirmation':
        if (!value.trim()) return t({ id: 'formError.passwordConfirmation.cantBeBlank' });
        if (fields.password && fields.password !== value) return t({ id: 'formError.passwordConfirmation.doesNotMatchConfirmation' });
        return '';
      default:
        return '';
    }
  };

  // 📝 Validate form
  const validateForm = useCallback(() => {
    let newErrors = {};

    const errorFirstname = validateField('firstname', fields.firstname);
    if (errorFirstname) newErrors = { ...newErrors, firstname: errorFirstname };

    const errorLastname = validateField('lastname', fields.lastname);
    if (errorLastname) newErrors = { ...newErrors, lastname: errorLastname };

    const errorEmail = validateField('email', fields.email);
    if (errorEmail) newErrors = { ...newErrors, email: errorEmail };

    const errorPhone = validateField('phone', fields.phone);
    if (errorPhone) newErrors = { ...newErrors, phone: errorPhone };

    const errorPassword = validateField('password', fields.password);
    if (errorPassword) newErrors = { ...newErrors, password: errorPassword };

    const errorPasswordConfirmation = validateField('passwordConfirmation', fields.passwordConfirmation);
    if (errorPasswordConfirmation) newErrors = { ...newErrors, passwordConfirmation: errorPasswordConfirmation };

    setClientErrors(newErrors);
    const isValidated = Object.keys(newErrors).length === 0;
    return isValidated;
  }, [fields, validateField]);

  // 🔁 Change field
  const changeField = ({ name, value }) => {
    setFields((prev) => ({ ...prev, [name]: value }));
    if (clientErrors[name]) {
      setClientErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const resetFields = () => {
    setFields(INITIAL_FIELDS);
    setClientErrors(INITIAL_ERRORS);
  };

  // 📬 Submit
  const handleSubmit = async () => {
    const isValidated = validateForm();
    if (!isValidated) return;

    setSubmitStatus(null);

    const variables = {
      input: {
        slugId,
        ...(link && { link }), // Optional only if customer invitation
        newAccount: {
          firstname: fields.firstname,
          lastname: fields.lastname,
          email: fields.email,
          locale,
          phone: fields.phone,
          password: fields.password,
          passwordConfirmation: fields.passwordConfirmation,
        },
      },
    };

    // 🚀 Request
    dispatch({
      type: 'SIGN_UP',
      status: null,
    });
    await signUp(variables);
  };

  // Manage success or error
  if (mutationData && submitStatus !== 'success') {
    // ✅ Sign up
    if (mutationData.signUp) {
      setSubmitStatus('success');
      resetFields();
      dispatch({
        type: 'SIGN_UP',
        status: 'WAITING_EMAIL_VALIDATION',
      });
    }

    // ✅ Sign up after manager invitation
    // 1️⃣ Using same email as validation
    if (mutationData.validateCustomerAccountLink === 'OK_NO_VALIDATION') {
      setSubmitStatus('success');
      resetFields();
      dispatch({
        type: 'SIGN_UP',
        status: 'OK_NO_VALIDATION',
      });
      handleFormTypeChange('signin');
    }
    // 2️⃣ Using different email as validation
    if (mutationData.validateCustomerAccountLink === 'OK') {
      setSubmitStatus('success');
      resetFields();
      dispatch({
        type: 'SIGN_UP',
        status: 'WAITING_EMAIL_VALIDATION',
      });
    }

    // ❌ Link invalid
    if (mutationData.validateCustomerAccountLink === 'INVALID') {
      setSubmitStatus('success');
      resetFields();
      dispatch({
        type: 'SIGN_UP',
        status: 'INVALID',
      });
    }
  }

  /* 🔴 Errors */
  useEffect(() => {
    if (mutationErrors) {
      setSubmitStatus('error');

      // 🛜 Network error
      if (mutationErrors.network && !toast) {
        showToast({
          message: t({ id: 'formError.signUpGlobalError' }),
          type: 'alert',
        });
      }

      // 📝 Global form error
      if (mutationErrors.global) {
        showToast({
          message: t({ id: 'formError.signUpGlobalError' }),
          type: 'alert',
        });
      }

      // 🔒 Bad credentials errors
      if (mutationErrors.signUp) {
        showToast({
          message: t({ id: 'formError.signUpGlobalError' }),
          type: 'alert',
        });
      }
    }
  }, [_get(mutationErrors, 'global', null) || _get(mutationErrors, 'signUp', null)]);

  return {
    fields,
    changeField,
    resetFields,
    canSubmit,
    submitStatus,
    setSubmitStatus,

    errors: { ...mutationErrors, ...clientErrors },
    isSubmitting: mutationLoading,

    handleSubmit,
  };
};



// WEBPACK FOOTER //
// ./routes/login/components/cpm_client/components/SignUp/useSignUp.js